import React, { useEffect, useRef, useState } from 'react';

const FONT_SIZES = [
  { label: 'Small', value: 'small', style: { fontSize: '0.875rem' } },
  { label: 'Normal', value: 'normal', style: { fontSize: '1.125rem' } },
  { label: 'Large', value: 'large', style: { fontSize: '1.5rem' } },
  { label: 'Huge', value: 'huge', style: { fontSize: '2.25rem' } },
];

const FONT_SIZE_MAP = {
  small: '0.875rem',
  normal: '1.125rem',
  large: '1.5rem',
  huge: '2.25rem',
};

const COLORS = [
  '#000000', '#ffffff', '#888888', '#ff0000', '#ffa500', '#ffff00', '#008000', '#00bfff', '#0000ff', '#800080',
  '#ffb6c1', '#ffe4b5', '#f5fffa', '#f5f5dc', '#a0522d', '#f4a460', '#228b22', '#20b2aa', '#9370db', '#4b0082',
  '#808080', '#c0c0c0', '#b8860b', '#ff6347', '#ffd700', '#90ee90', '#4682b4', '#1e90ff', '#b22222', '#9400d3',
  '#a9a9a9', '#d3d3d3', '#cd853f', '#bc8f8f', '#daa520', '#eee8aa', '#8fbc8f', '#2e8b57', '#483d8b', '#191970',
];

interface CheckoutTextProps {
  selected: boolean;
  config: {
    background?: string;
    borderColor?: string;
    borderWidth?: number;
    radius?: number;
    text?: string;
    fontColor?: string;
    fontSize?: 'small' | 'normal' | 'large' | 'huge';
    textAlign?: 'left' | 'center' | 'right';
  };
  onSelect: () => void;
  onConfigChange?: (cfg: Partial<CheckoutTextProps['config']>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  dragHandleProps?: any;
  viewMode?: boolean; // Modo visualização (não editável)
}

const CheckoutText: React.FC<CheckoutTextProps> = ({ selected, config, onSelect, onConfigChange, onDelete, onDuplicate, dragHandleProps, viewMode = false }) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [showFontSize, setShowFontSize] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  // Use config ou valor padrão
  const text = config.text ?? 'Título';
  const fontSize = config.fontSize ?? 'huge';
  const fontColor = config.fontColor ?? '#222';
  const textAlign = config.textAlign ?? 'left';

  // Se estiver em viewMode, renderizar apenas o conteúdo sem interações
  if (viewMode) {
    // Sem fundo e sem borda o texto não tem caixa, então não leva padding:
    // o espaço até os outros componentes vem só do espaçamento único do checkout.
    const hasBox = (!!config.background && config.background !== 'transparent') || Number(config.borderWidth) > 0;
    return (
      <span
        className={`block font-bold w-full ${hasBox ? 'p-4' : ''}`}
        style={{
          fontSize: FONT_SIZE_MAP[fontSize],
          color: fontColor,
          textAlign: textAlign,
          background: config.background || 'transparent',
          border: `solid ${config.borderWidth || 0}px ${config.borderColor || 'transparent'}`,
          borderRadius: config.radius ? `${config.radius}px` : '8px',
        }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }

  // Função para aplicar/remover formatação usando execCommand
  const applyFormat = (command: 'bold' | 'italic' | 'underline') => {
    document.execCommand(command);
    if (textRef.current && onConfigChange) {
      onConfigChange({ text: textRef.current.innerHTML });
    }
  };

  // Formatação: aplica a todo o texto
  const handleFontSize = (value: 'small' | 'normal' | 'large' | 'huge') => {
    if (onConfigChange) onConfigChange({ fontSize: value });
    setShowFontSize(false);
  };
  const handleColor = (color: string) => {
    if (onConfigChange) onConfigChange({ fontColor: color });
    setShowColorPicker(false);
  };
  const handleAlign = (align: 'left' | 'center' | 'right') => {
    if (onConfigChange) onConfigChange({ textAlign: align });
  };

  // Keep inline editable content in sync with external updates (Inspector changes)
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const isFocused = document.activeElement === el;
    if (!isFocused && el.innerHTML !== (config.text ?? 'Título')) {
      el.innerHTML = config.text ?? 'Título';
    }
  }, [config.text]);

  return (
    <div
      onClick={onSelect}
      className={`relative group cursor-pointer w-full h-full ${selected ? '' : 'hover:ring-2 hover:ring-blue-300'}`}
      style={{
        background: config.background || 'transparent',
        border: `solid ${config.borderWidth || 0}px ${config.borderColor || 'transparent'}`,
        borderRadius: config.radius ? `${config.radius}px` : '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Ações: excluir, duplicar, arrastar */}
      {selected && (
        <div className="absolute right-2 top-2 z-20 select-none" style={{ pointerEvents: 'auto' }}>
          <div className="flex items-center bg-indigo-600 text-white rounded-md shadow overflow-hidden">
            {/* Excluir */}
            <button
              type="button"
              title="Excluir"
              className="px-3 py-1.5 hover:bg-indigo-500"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setConfirmDeleteOpen(true);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 3h6m-9 4h12m-10 0v12m8-12v12M9 3l1 1h4l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
            {/* Duplicar */}
            <button
              type="button"
              title="Duplicar"
              className="px-3 py-1.5 hover:bg-indigo-500"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDuplicate && onDuplicate();
              }}
            >
              {/* ícone dois quadrados sobrepostos (duplicar) */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="7" y="7" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                <rect x="3" y="3" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            {/* Arrastar */}
            <div
              {...(dragHandleProps || {})}
              title="Arrastar"
              className="px-3 py-1.5 cursor-grab hover:bg-indigo-500"
              onMouseDown={(e) => {
                // permitir drag sem selecionar/deselecionar
                e.stopPropagation();
              }}
            >
              {/* ícone de expandir com setas (drag) */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3H3v5M21 8V3h-5M3 16v5h5M16 21h5v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 7l-4-4M17 7l4-4M7 17l-4 4M17 17l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>
      )}
      {/* Barra flutuante de formatação */}
      {selected && showToolbar && (
        <div className="absolute -top-10 left-4 z-10 flex items-center gap-2 bg-gray-900 text-white rounded shadow px-2 py-1">
          <button onMouseDown={e => { e.preventDefault(); applyFormat('bold'); }} className="px-1"><b>B</b></button>
          <button onMouseDown={e => { e.preventDefault(); applyFormat('italic'); }} className="px-1 italic">I</button>
          <button onMouseDown={e => { e.preventDefault(); applyFormat('underline'); }} className="px-1 underline">U</button>
          {/* Tamanho da fonte */}
          <div className="relative">
            <button onMouseDown={e => { e.preventDefault(); setShowFontSize(v => !v); }} className="px-1">
              {FONT_SIZES.find(fs => fs.value === fontSize)?.label} ▼
            </button>
            {showFontSize && (
              <div className="absolute left-0 top-7 bg-gray-800 text-white rounded shadow flex flex-col min-w-[100px] z-20">
                {FONT_SIZES.map(fs => (
                  <button key={fs.value} onMouseDown={e => { e.preventDefault(); handleFontSize(fs.value as any); }} style={fs.style} className="px-3 py-1 text-left hover:bg-gray-700">{fs.label}</button>
                ))}
              </div>
            )}
          </div>
          {/* Cor do texto */}
          <div className="relative">
            <button onMouseDown={e => { e.preventDefault(); setShowColorPicker(v => !v); }} className="px-1"><span className="inline-block w-50 h-4 rounded align-middle border border-white" style={{ background: fontColor }}></span></button>
            {showColorPicker && (
              <div className="absolute left-0 top-7 bg-gray-800 text-white rounded shadow grid grid-cols-8 gap-1 p-2 z-20">
                {COLORS.map(color => (
                  <button key={color} onMouseDown={e => { e.preventDefault(); handleColor(color); }} className="w-5 h-5 rounded border border-gray-400" style={{ background: color }} />
                ))}
              </div>
            )}
          </div>
          <button onMouseDown={e => { e.preventDefault(); handleAlign('left'); }} className={`px-1 ${textAlign === 'left' ? 'bg-gray-700' : ''}`} title="Alinhar à esquerda">≡</button>
          <button onMouseDown={e => { e.preventDefault(); handleAlign('center'); }} className={`px-1 ${textAlign === 'center' ? 'bg-gray-700' : ''}`} title="Centralizar">≡</button>
          <button onMouseDown={e => { e.preventDefault(); handleAlign('right'); }} className={`px-1 ${textAlign === 'right' ? 'bg-gray-700' : ''}`} title="Alinhar à direita">≡</button>
        </div>
      )}
      {selected ? (
        <span
          className="block font-bold p-4 outline-none min-h-[2rem] w-full flex-grow"
          contentEditable
          suppressContentEditableWarning
          style={{
            fontSize: FONT_SIZE_MAP[fontSize],
            color: fontColor,
            textAlign: textAlign,
            background: 'transparent',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            height: '100%'
          }}
          onInput={e => onConfigChange && onConfigChange({ text: (e.target as HTMLElement).innerHTML })}
          onFocus={() => setShowToolbar(true)}
          onBlur={() => setShowToolbar(false)}
          ref={(el) => {
            if (el) {
              textRef.current = el;
              // Definir HTML inicial
              if (el.innerHTML === '' && text) {
                el.innerHTML = text;
              }
            }
          }}
          spellCheck={false}
        />
      ) : (
        <span
          className="block font-bold p-4 min-h-[2rem] w-full flex-grow"
          style={{
            fontSize: FONT_SIZE_MAP[fontSize],
            color: fontColor,
            textAlign: textAlign,
            height: '100%'
          }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      )}
      {/* Borda de seleção removida para implementar apenas no hover */}

      {/* Modal de confirmação de exclusão */}
      {confirmDeleteOpen && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="absolute inset-0 bg-black/60" onMouseDown={(e) => { e.stopPropagation(); }} />
          <div className="relative z-40 w-[90%] sm:w-[420px] mx-auto">
            <div className="flex flex-col items-stretch gap-3">
              <button
                type="button"
                className="w-full py-3 rounded-md bg-red-600 text-white font-medium shadow hover:bg-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteOpen(false);
                  if (onDelete) onDelete();
                }}
              >
                Excluir componente
              </button>
              <button
                type="button"
                className="w-full py-3 rounded-md bg-white text-gray-900 font-medium shadow border border-gray-200 hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDeleteOpen(false);
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutText;
